const DEFAULT_OPTIMIZE_REQUEST_URL = "/api/optimize";
const DEFAULT_JOB_POLL_URL = "/api/optimization/status/current";
const DEFAULT_RESULT_URL = "/results";
const DEFAULT_POLL_INTERVAL_MS = 2000;

import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { appendDispatchDateToUrl } from "./dispatchDateUrls.js";
import { normalizeOptimizationMode } from "./optimizationModes.js";

function getDefaultFetch() {
  if (typeof globalThis.fetch !== "function") {
    throw new Error("Lancement d'optimisation indisponible: fetch n'est pas supporté.");
  }

  return globalThis.fetch.bind(globalThis);
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function resolveLaunchErrorMessage(payload, fallbackMessage) {
  if (!payload || typeof payload !== "object") {
    return fallbackMessage;
  }

  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error;
  }

  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  if (payload.detail && typeof payload.detail === "object" && !Array.isArray(payload.detail)) {
    for (const key of ["message", "error", "detail"]) {
      const detailMessage = payload.detail[key];
      if (typeof detailMessage === "string" && detailMessage.trim()) {
        return detailMessage;
      }
    }
  }

  return fallbackMessage;
}

function normalizeOptionalCount(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return Math.round(numericValue);
}

function normalizeOptionalDurationMs(value) {
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return Math.round(value * 10) / 10;
}

function buildOptimizationLaunchPreflightFailure({
  code,
  detailItems = [],
  filterKey = "",
  message,
  title,
}) {
  return {
    ok: false,
    issue: {
      code,
      detailItems,
      filterKey,
      message,
      title,
    },
  };
}

export function resolveOptimizationLaunchPreflight({
  allowEveningOrderPool = false,
  includeSuspendedDeliveries = false,
  isOptimizing = false,
  pendingPlanningMutationCount = null,
  routingAddressCount = null,
  suspendedDeliveryCount = null,
  technicalPrecheckCount = null,
  totalOrders = null,
  totalTrackedFleet = null,
} = {}) {
  if (isOptimizing) {
    return buildOptimizationLaunchPreflightFailure({
      code: "already-running",
      detailItems: [
        {
          key: "next-step",
          label: "Étape suivante",
          value: "Attendre la fin du calcul en cours avant de relancer l'optimisation.",
        },
      ],
      message: "Une optimisation est déjà en cours. Attendez la fin du calcul avant de relancer.",
      title: "Optimisation déjà en cours",
    });
  }

  const normalizedPendingPlanningMutationCount = normalizeOptionalCount(pendingPlanningMutationCount);
  if (normalizedPendingPlanningMutationCount && normalizedPendingPlanningMutationCount > 0) {
    return buildOptimizationLaunchPreflightFailure({
      code: "pending-planning-mutations",
      detailItems: [
        {
          key: "planning-mutations",
          label: "Mises à jour en cours",
          value: String(normalizedPendingPlanningMutationCount),
        },
        {
          key: "next-step",
          label: "Étape suivante",
          value: "Attendre la fin de la libération, réaffectation ou suspension en cours avant de relancer l'optimisation.",
        },
      ],
      message: "Une mise à jour du planning manuel est encore en cours. Attendez sa fin avant de relancer l'optimisation.",
      title: "Mise à jour du planning en cours",
    });
  }

  const normalizedOrderCount = normalizeOptionalCount(totalOrders);
  const normalizedSuspendedDeliveryCount = normalizeOptionalCount(suspendedDeliveryCount);
  const hasIncludedSuspendedDeliveries = includeSuspendedDeliveries === true
    && normalizedSuspendedDeliveryCount !== null
    && normalizedSuspendedDeliveryCount > 0;
  if (normalizedOrderCount === 0 && !hasIncludedSuspendedDeliveries && !allowEveningOrderPool) {
    return buildOptimizationLaunchPreflightFailure({
      code: "missing-orders",
      detailItems: [
        {
          key: "orders",
          label: "Commandes visibles",
          value: "0",
        },
        {
          key: "next-step",
          label: "Étape suivante",
          value: "Importer ou recharger au moins une commande avant de relancer l'optimisation.",
        },
      ],
      message: "Aucune commande visible n'est disponible pour l'optimisation.",
      title: "Aucune commande à optimiser",
    });
  }

  const normalizedTruckCount = normalizeOptionalCount(totalTrackedFleet);
  if (normalizedTruckCount === 0) {
    return buildOptimizationLaunchPreflightFailure({
      code: "missing-trucks",
      detailItems: [
        {
          key: "trucks",
          label: "Camions suivis",
          value: "0",
        },
        {
          key: "next-step",
          label: "Étape suivante",
          value: "Importer ou recharger au moins un camion avant de relancer l'optimisation.",
        },
      ],
      message: "Aucun camion suivi n'est disponible pour l'optimisation.",
      title: "Aucun camion à optimiser",
    });
  }

  return {
    ok: true,
    issue: null,
  };
}

export async function launchOptimizationViaApi({
  dispatchDate = undefined,
  dispatchPeriod = undefined,
  fetchImpl = getDefaultFetch(),
  includeSuspendedDeliveries = false,
  optimizationMode = null,
  requestHeaders = null,
  requestUrl = DEFAULT_OPTIMIZE_REQUEST_URL,
} = {}) {
  const scopedRequestUrl = appendDispatchDateToUrl(requestUrl, dispatchDate, dispatchPeriod);
  const isPlanningResetRequest = String(requestUrl || "").includes("/api/planning/reset");
  const requestStartedAtEpochMs = Date.now();
  const requestStartedAtPerf = typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : null;
  const normalizedOptimizationMode = typeof optimizationMode === "string" && optimizationMode.trim()
    ? normalizeOptimizationMode(optimizationMode, "")
    : "";
  const requestPayload = {
    ...(normalizedOptimizationMode ? { optimization_mode: normalizedOptimizationMode } : {}),
    ...(includeSuspendedDeliveries === true ? { include_suspended_deliveries: true } : {}),
  };
  const hasRequestPayload = Object.keys(requestPayload).length > 0;
  const response = await fetchWithSupabaseAuth(scopedRequestUrl, {
    fetchImpl,
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(hasRequestPayload ? { "Content-Type": "application/json" } : {}),
      "X-Optimization-Diagnostics": "1",
      ...(requestHeaders && typeof requestHeaders === "object" && !Array.isArray(requestHeaders)
        ? requestHeaders
        : {}),
    },
    ...(hasRequestPayload ? { body: JSON.stringify(requestPayload) } : {}),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    const launchError = new Error(resolveLaunchErrorMessage(
      payload,
      isPlanningResetRequest
        ? "Le lancement de la réinitialisation du planning a échoué."
        : "Le lancement de l'optimisation a échoué.",
    ));
    const detail = payload?.detail && typeof payload.detail === "object" && !Array.isArray(payload.detail)
      ? payload.detail
      : null;
    const diagnostics = payload?._optimization_diagnostics
      && typeof payload._optimization_diagnostics === "object"
      && !Array.isArray(payload._optimization_diagnostics)
      ? payload._optimization_diagnostics
      : null;
    const reasonCode = [payload?.reason_code, payload?.code, detail?.reason_code, detail?.code]
      .find((value) => typeof value === "string" && value.trim());
    const failedStage = [diagnostics?.failed_stage, payload?.failed_stage, detail?.failed_stage]
      .find((value) => typeof value === "string" && value.trim());
    launchError.status = response.status;
    launchError.requestPath = String(requestUrl || "").split("?", 1)[0];
    launchError.reasonCode = reasonCode?.trim?.() || "";
    launchError.failedStage = failedStage?.trim?.() || "";
    launchError.correlationId = response.headers?.get?.("X-Correlation-ID")
      || response.headers?.get?.("X-Request-ID")
      || "";
    throw launchError;
  }

  const acceptedAtEpochMs = Date.now();
  const acceptedAtPerf = typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : null;

  const tracking = payload && typeof payload.tracking === "object"
    ? payload.tracking
    : {};
  const latestOptimization = payload && typeof payload.latest_optimization === "object"
    ? payload.latest_optimization
    : {};
  const trackingMode = typeof tracking.mode === "string" && tracking.mode.trim()
    ? tracking.mode
    : "poll_job_status";
  const pollUrl = typeof tracking.poll_url === "string" && tracking.poll_url.trim()
    ? tracking.poll_url
    : DEFAULT_JOB_POLL_URL;
  const rawResultUrl = typeof tracking.html_result_url === "string" && tracking.html_result_url.trim()
    ? tracking.html_result_url
    : (typeof latestOptimization.href === "string" && latestOptimization.href.trim()
      ? latestOptimization.href
      : DEFAULT_RESULT_URL);
  const trackingDispatchDate = typeof tracking.dispatch_date === "string" && tracking.dispatch_date.trim()
    ? tracking.dispatch_date.trim()
    : null;
  const trackingDispatchPeriod = typeof tracking.dispatch_period === "string" && tracking.dispatch_period.trim()
    ? tracking.dispatch_period.trim().toLowerCase()
    : null;
  const hasCancellationAllowed = Object.prototype.hasOwnProperty.call(tracking, "cancellation_allowed")
    || Object.prototype.hasOwnProperty.call(tracking, "cancellationAllowed");
  const cancellationAllowed = tracking.cancellation_allowed === true || tracking.cancellationAllowed === true;
  const resultUrl = appendDispatchDateToUrl(
    rawResultUrl,
    trackingDispatchDate || undefined,
    trackingDispatchPeriod || undefined,
  );
  const recommendedPollIntervalMs = typeof tracking.recommended_poll_interval_ms === "number"
    && Number.isFinite(tracking.recommended_poll_interval_ms)
    && tracking.recommended_poll_interval_ms > 0
    ? tracking.recommended_poll_interval_ms
    : DEFAULT_POLL_INTERVAL_MS;
  const resolvedOptimizationMode = typeof tracking.optimization_mode === "string" && tracking.optimization_mode.trim()
    ? tracking.optimization_mode.trim().toLowerCase()
    : normalizedOptimizationMode;
  const operationKind = tracking.operation_kind === "planning_reset"
    ? "planning_reset"
    : isPlanningResetRequest
      ? "planning_reset"
      : "optimization";

  return {
    jobId: typeof tracking.job_id === "string" && tracking.job_id.trim()
      ? tracking.job_id.trim()
      : null,
    dispatchDate: trackingDispatchDate,
    dispatchPeriod: trackingDispatchPeriod,
    operationKind,
    alreadyRunning: tracking.already_running === true,
    ...(hasCancellationAllowed ? { cancellationAllowed } : {}),
    message: typeof payload?.message === "string" && payload.message.trim()
      ? payload.message
      : operationKind === "planning_reset"
        ? "Réinitialisation du planning démarrée en arrière-plan."
        : "Optimisation démarrée en arrière-plan.",
    asyncReady: tracking.async_ready === true,
    executionStrategy: typeof tracking.execution_strategy === "string" && tracking.execution_strategy.trim()
      ? tracking.execution_strategy.trim()
      : null,
    optimizationMode: resolvedOptimizationMode || null,
    payload,
    pollUrl,
    recommendedPollIntervalMs,
    requestedOptimizationMode: typeof tracking.requested_optimization_mode === "string" && tracking.requested_optimization_mode.trim()
      ? tracking.requested_optimization_mode.trim().toLowerCase()
      : (normalizedOptimizationMode || null),
    resultUrl,
    solverTimeoutSeconds: typeof tracking.solver_timeout_seconds === "number" && Number.isFinite(tracking.solver_timeout_seconds)
      ? Math.round(tracking.solver_timeout_seconds)
      : null,
    status: typeof payload?.status === "string" && payload.status.trim()
      ? payload.status.trim().toLowerCase()
      : "accepted",
    timing: {
      acceptedAt: new Date(acceptedAtEpochMs).toISOString(),
      acceptedAtEpochMs,
      postOptimizeRoundtripMs: requestStartedAtPerf != null && acceptedAtPerf != null
        ? normalizeOptionalDurationMs(acceptedAtPerf - requestStartedAtPerf)
        : null,
      requestStartedAtEpochMs,
    },
    trackingMode,
    usedDefaultOptimizationMode: tracking.used_default_optimization_mode === true,
  };
}
